import React, { useState, useEffect } from 'react';
import axios from '../axios-config';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import DocumentUploader from '../components/DocumentUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALLOWED_BUSINESS_TYPES = [
  'Proprietorship', 'Partnership', 'LLP',
  'Private Limited', 'OPC',
  'Individual Service Provider', 'Other',
];

const CATEGORY_OPTIONS = [
  'Housekeeping AMC', 'Security Services', 'Lift AMC', 'CCTV AMC', 'Fire Safety Systems',
  'Plumbing Works', 'Electrical Works', 'Painting Works', 'Waterproofing Works',
  'Pest Control Services', 'Civil Repair Works', 'STP/WTP Maintenance', 'Generator AMC',
  'Solar Maintenance', 'Miscellaneous Services',
];

export default function SupplierProfileEdit() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disclaimers, setDisclaimers] = useState(null);
  const [form, setForm] = useState({
    company_name: '', contact_person: '', mobile: '', business_type: '',
    address: '', city: '', state: '', district: '',
    categories: [], service_area: '', website: '', pan: '',
    gst_registered: null, gst_number: '', gst_certificate_url: '',
    license_available: null, license_type: '', license_number: '',
    license_valid_till: '', license_doc_url: '', license_declaration_accepted: false,
    company_intro: '', years_of_experience: '', services_offered: [],
    team_strength: '', major_clients: [], certifications: [], documents: [],
  });

  useEffect(() => {
    (async () => {
      try {
        const [meRes, disRes] = await Promise.all([
          axios.get(`${API}/suppliers/me`),
          axios.get(`${API}/disclaimers`),
        ]);
        setDisclaimers(disRes.data);
        const sup = meRes.data || {};
        setForm(prev => ({
          ...prev,
          ...sup,
          categories: sup.categories || [],
          services_offered: sup.services_offered || [],
          major_clients: sup.major_clients || [],
          certifications: sup.certifications || [],
          documents: sup.documents || [],
        }));
      } catch (e) {
        toast.error('Failed to load profile');
      } finally { setLoading(false); }
    })();
  }, []);

  const isHighRisk = (form.categories || []).some(c => (disclaimers?.high_risk_categories || []).includes(c));

  const save = async () => {
    if (form.gst_registered === true && !form.gst_number) {
      toast.error('Please enter GST number');
      return;
    }
    if (form.license_available === true && !form.license_number) {
      toast.error('Please enter License number');
      return;
    }
    if (form.license_available === false && !form.license_declaration_accepted) {
      toast.error('Please accept the no-license declaration');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        years_of_experience: form.years_of_experience ? Number(form.years_of_experience) : null,
        team_strength: form.team_strength ? Number(form.team_strength) : null,
      };
      const { data } = await axios.put(`${API}/suppliers/me`, payload);
      setForm(prev => ({ ...prev, ...data }));
      toast.success('Profile updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;

  const inputCls = 'w-full px-3 py-3 rounded border border-input bg-background text-sm';
  const labelCls = 'block text-sm font-medium text-navy mb-1.5';

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="bg-navy text-white sticky top-0 z-30">
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/supplier-dashboard')} className="text-white/80 hover:text-white inline-flex items-center text-sm" data-testid="supplier-profile-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </button>
          <div className="font-bold text-sm sm:text-base">Edit Capability Profile</div>
          <button onClick={save} disabled={saving} className="px-3 py-1.5 bg-gold text-navy font-bold text-xs rounded inline-flex items-center disabled:opacity-50" data-testid="supplier-profile-save-btn">
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </nav>
      </header>

      <div className="container mx-auto px-4 py-5 max-w-3xl space-y-6">
        {/* Disclaimer A */}
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-[11px] text-amber-900" data-testid="supplier-profile-disclaimer">
          <AlertTriangle className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
          {disclaimers?.disclaimers?.supplier_profile}
        </div>

        {/* High-risk warning */}
        {isHighRisk && (
          <div className="bg-red-50 border-2 border-red-300 rounded p-3 text-[11px] text-red-900" data-testid="supplier-profile-high-risk-warning">
            <AlertTriangle className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
            <strong>High-Risk Category:</strong> {disclaimers?.disclaimers?.high_risk_category}
          </div>
        )}

        {/* Module 1 — Identity */}
        <Section title="Business Identity" testid="section-identity">
          <Field label="Company / Business Name *">
            <input type="text" value={form.company_name || ''} onChange={e => setForm({ ...form, company_name: e.target.value })} className={inputCls} data-testid="profile-company-name-input" />
          </Field>
          <Field label="Contact Person">
            <input type="text" value={form.contact_person || ''} onChange={e => setForm({ ...form, contact_person: e.target.value })} className={inputCls} data-testid="profile-contact-person-input" />
          </Field>
          <Field label="Mobile">
            <input type="tel" value={form.mobile || ''} onChange={e => setForm({ ...form, mobile: e.target.value })} className={inputCls} data-testid="profile-mobile-input" />
          </Field>
          <Field label="Business Type *">
            <select value={form.business_type || ''} onChange={e => setForm({ ...form, business_type: e.target.value })} className={inputCls} data-testid="profile-business-type-select">
              <option value="">Select…</option>
              {ALLOWED_BUSINESS_TYPES.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="Address">
            <textarea value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} className={inputCls} rows={2} data-testid="profile-address-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City"><input type="text" value={form.city || ''} onChange={e => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="profile-city-input" /></Field>
            <Field label="State"><input type="text" value={form.state || ''} onChange={e => setForm({ ...form, state: e.target.value })} className={inputCls} data-testid="profile-state-input" /></Field>
          </div>
        </Section>

        {/* GST */}
        <Section title="GST Registration" testid="section-gst">
          <Field label="GST Registered?">
            <div className="flex gap-4" data-testid="profile-gst-radio">
              <Radio name="gst_registered" checked={form.gst_registered === true} label="Yes" onChange={() => setForm({ ...form, gst_registered: true })} testid="profile-gst-yes" />
              <Radio name="gst_registered" checked={form.gst_registered === false} label="No (Unregistered)" onChange={() => setForm({ ...form, gst_registered: false, gst_number: '', gst_certificate_url: '' })} testid="profile-gst-no" />
            </div>
          </Field>
          {form.gst_registered === true && (
            <>
              <Field label="GST Number *">
                <input type="text" value={form.gst_number || ''} onChange={e => setForm({ ...form, gst_number: e.target.value.toUpperCase() })} className={inputCls} data-testid="profile-gst-number-input" />
              </Field>
              <Field label="GST Certificate">
                <DocumentUploader purpose="gst_certificate" maxFiles={1} files={form.gst_certificate_url ? [{ url: form.gst_certificate_url, filename: 'GST Certificate' }] : []} onChange={(arr) => setForm({ ...form, gst_certificate_url: arr[0]?.url || '' })} />
              </Field>
            </>
          )}
          {form.gst_registered === false && (
            <div className="text-[11px] text-muted-foreground bg-muted/40 border border-border rounded p-2" data-testid="profile-gst-unregistered-note">
              GST Status: <strong>Unregistered</strong> — will be displayed on your public profile.
            </div>
          )}
          <Field label="PAN (Preferred, optional)">
            <input type="text" value={form.pan || ''} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} className={inputCls} maxLength={10} data-testid="profile-pan-input" />
          </Field>
        </Section>

        {/* License & Certification */}
        <Section title="License & Certification Disclosure" testid="section-license">
          <Field label="Do you currently hold a formal License/Certification for the selected service category?">
            <div className="flex gap-4" data-testid="profile-license-radio">
              <Radio name="license_available" checked={form.license_available === true} label="Yes" onChange={() => setForm({ ...form, license_available: true, license_declaration_accepted: false })} testid="profile-license-yes" />
              <Radio name="license_available" checked={form.license_available === false} label="No" onChange={() => setForm({ ...form, license_available: false, license_type: '', license_number: '', license_valid_till: '', license_doc_url: '' })} testid="profile-license-no" />
            </div>
          </Field>

          {form.license_available === true && (
            <>
              <Field label="License Type *">
                <input type="text" value={form.license_type || ''} onChange={e => setForm({ ...form, license_type: e.target.value })} placeholder="e.g., Electrical Contractor License" className={inputCls} data-testid="profile-license-type-input" />
              </Field>
              <Field label="License Number *">
                <input type="text" value={form.license_number || ''} onChange={e => setForm({ ...form, license_number: e.target.value })} className={inputCls} data-testid="profile-license-number-input" />
              </Field>
              <Field label="Valid Till">
                <input type="date" value={form.license_valid_till || ''} onChange={e => setForm({ ...form, license_valid_till: e.target.value })} className={inputCls} data-testid="profile-license-valid-till-input" />
              </Field>
              <Field label="Upload License Copy">
                <DocumentUploader purpose="license_certificate" maxFiles={1} files={form.license_doc_url ? [{ url: form.license_doc_url, filename: 'License Copy' }] : []} onChange={(arr) => setForm({ ...form, license_doc_url: arr[0]?.url || '' })} />
              </Field>
            </>
          )}

          {form.license_available === false && (
            <div className="bg-amber-50 border border-amber-300 rounded p-3 text-[11px] text-amber-900 space-y-2" data-testid="profile-license-declaration-box">
              <div><strong>Disclaimer:</strong> {disclaimers?.disclaimers?.unlicensed_supplier}</div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.license_declaration_accepted === true} onChange={e => setForm({ ...form, license_declaration_accepted: e.target.checked })} className="mt-0.5" data-testid="profile-license-declaration-checkbox" />
                <span>I confirm: "I currently do not possess a formal license or certification for the selected service category." I will rely on experience-based service delivery.</span>
              </label>
            </div>
          )}
        </Section>

        {/* Module 2 — Capability */}
        <Section title="Capability Profile" testid="section-capability">
          <Field label="Company Introduction">
            <textarea value={form.company_intro || ''} onChange={e => setForm({ ...form, company_intro: e.target.value })} className={inputCls} rows={3} placeholder="Brief intro about your business…" data-testid="profile-company-intro-input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Years of Experience"><input type="number" min="0" value={form.years_of_experience || ''} onChange={e => setForm({ ...form, years_of_experience: e.target.value })} className={inputCls} data-testid="profile-years-exp-input" /></Field>
            <Field label="Team Strength"><input type="number" min="0" value={form.team_strength || ''} onChange={e => setForm({ ...form, team_strength: e.target.value })} className={inputCls} data-testid="profile-team-strength-input" /></Field>
          </div>
          <Field label="Service Categories (select all that apply)">
            <CategoryChips selected={form.categories} options={CATEGORY_OPTIONS} onChange={(arr) => setForm({ ...form, categories: arr })} testid="profile-categories" />
          </Field>
          <Field label="Service Areas (cities / districts you cover)">
            <input type="text" value={form.service_area || ''} onChange={e => setForm({ ...form, service_area: e.target.value })} placeholder="e.g., Mumbai, Pune, Thane" className={inputCls} data-testid="profile-service-area-input" />
          </Field>
          <Field label="Services Offered">
            <TextArrayInput items={form.services_offered} onChange={(arr) => setForm({ ...form, services_offered: arr })} placeholder="Add a service…" testid="profile-services-offered" />
          </Field>
          <Field label="Major Clients">
            <TextArrayInput items={form.major_clients} onChange={(arr) => setForm({ ...form, major_clients: arr })} placeholder="Add a client name…" testid="profile-major-clients" />
          </Field>
          <Field label="Certifications">
            <TextArrayInput items={form.certifications} onChange={(arr) => setForm({ ...form, certifications: arr })} placeholder="e.g., ISO 9001:2015" testid="profile-certifications" />
          </Field>
        </Section>
      </div>
    </div>
  );
}

const Section = ({ title, testid, children }) => (
  <div className="bg-card border border-border rounded-lg p-4 space-y-4" data-testid={testid}>
    <h3 className="text-sm font-bold text-navy uppercase tracking-wide">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-navy mb-1.5">{label}</label>
    {children}
  </div>
);

const Radio = ({ name, checked, label, onChange, testid }) => (
  <label className="inline-flex items-center text-sm cursor-pointer">
    <input type="radio" name={name} checked={checked} onChange={onChange} className="mr-1.5 w-4 h-4" data-testid={testid} /> {label}
  </label>
);

const CategoryChips = ({ selected, options, onChange, testid }) => {
  const toggle = (c) => onChange(selected.includes(c) ? selected.filter(x => x !== c) : [...selected, c]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map(c => {
        const on = selected.includes(c);
        return (
          <button key={c} type="button" onClick={() => toggle(c)} className={`px-2.5 py-1.5 rounded-full text-[11px] border transition-colors ${on ? 'bg-navy text-white border-navy' : 'bg-card text-navy border-border'}`} data-testid={`cat-chip-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
            {c}
          </button>
        );
      })}
    </div>
  );
};

const TextArrayInput = ({ items, onChange, placeholder, testid }) => {
  const [text, setText] = useState('');
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex gap-2">
        <input type="text" value={text} onChange={e => setText(e.target.value)} placeholder={placeholder} className="flex-1 px-3 py-2 rounded border border-input bg-background text-sm" data-testid={`${testid}-input`} />
        <button type="button" onClick={() => { if (text.trim()) { onChange([...(items || []), text.trim()]); setText(''); } }} className="px-3 py-2 bg-navy text-white rounded text-xs inline-flex items-center" data-testid={`${testid}-add-btn`}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add
        </button>
      </div>
      {items && items.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it, idx) => (
            <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full bg-muted text-[11px] text-navy">
              {it}
              <button type="button" onClick={() => onChange(items.filter((_, i) => i !== idx))} className="ml-1.5 text-red-600" data-testid={`${testid}-remove-${idx}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
